// "Add pictures to a row" questions (24 Oct 2026, Year 1). The question shows
// a picture graph (1 picture = 1 thing) with one or more rows to fill, empty
// or part filled, and the child taps + and − to add pictures to each of
// them or take pictures away. It is sent and marked like a drawn column
// (buildGraphAnswer.ts): as strings the marker compares with the answer key
// as a set. So an answer and its key must be written the same way, and the
// functions here are the one place that decides how.
//
// - The question names the rows to fill: `fill` is a list of row labels.
// - One filled row is "row=<row>=<count>", the row counted from 0 down the
//   graph ("row=1=7").
//
// It cannot be mistaken for a drawn column ("bar="), a match ("pair=") or a
// several-boxes or sort entry, whose id before the "=" is a single letter.
import {
  readPracticeDiagram,
  type PracticeDiagramSpec,
} from './practice-diagram-data';

type PictureGraph = Extract<PracticeDiagramSpec, { type: 'picture_graph' }>;

export type FillRow = {
  // The row counted from 0, as text: "1".
  id: string;
  row: number;
  label: string;
  // How many pictures the row starts with.
  start: number;
};

export type BuildPictureGraphQuestion = {
  graph: PictureGraph;
  rows: FillRow[];
};

// A row holds at most this many pictures (a picture graph's own limit).
export const FILL_MOST = 12;
// At most this many rows to fill in one question.
const MAX_ROWS = 4;

function readPictureGraph(picture: unknown): PictureGraph | null {
  const diagram = readPracticeDiagram({ diagram: picture });
  if (diagram?.type !== 'picture_graph') return null;
  // One whole picture for one thing: no half pictures to add.
  return diagram.rows.every((row) => Number.isInteger(row.count))
    ? diagram
    : null;
}

function rowsOf(graph: PictureGraph, fill: unknown): FillRow[] | null {
  if (!Array.isArray(fill) || fill.length === 0 || fill.length > MAX_ROWS)
    return null;
  if (new Set(fill).size !== fill.length) return null;
  const rows: FillRow[] = [];
  for (const name of fill as unknown[]) {
    const row = graph.rows.findIndex((entry) => entry.label === name);
    const shown = graph.rows[row];
    if (!shown) return null;
    rows.push({
      id: String(row),
      row,
      label: shown.label,
      start: shown.count,
    });
  }
  return rows.sort((a, b) => a.row - b.row);
}

// The question's graph and rows to fill, or null.
export function readBuildPictureGraph(
  content: unknown,
): BuildPictureGraphQuestion | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  const graph = readPictureGraph(record.diagram);
  if (!graph) return null;
  const rows = rowsOf(graph, record.fill);
  return rows ? { graph, rows } : null;
}

export function rowEntry(id: string, count: number): string {
  return `row=${id}=${String(count)}`;
}

export function parseRowEntry(
  entry: unknown,
): { id: string; count: number } | null {
  if (typeof entry !== 'string') return null;
  const match = /^row=(\d{1,2})=(\d{1,3})$/.exec(entry);
  return match?.[1] ? { id: match[1], count: Number(match[2]) } : null;
}

// The rows as they are kept while a child works: "<row>=<count>" on each
// line. A row not listed still has its first pictures.
export function splitFilled(
  text: string,
  question: BuildPictureGraphQuestion,
): Map<string, number> {
  const counts = new Map(question.rows.map((row) => [row.id, row.start]));
  for (const line of text.split('\n')) {
    const parsed = parseRowEntry(`row=${line}`);
    if (parsed && counts.has(parsed.id) && parsed.count <= FILL_MOST)
      counts.set(parsed.id, parsed.count);
  }
  return counts;
}

// One press of + (1) or − (-1) on one row, as the new text.
export function stepFilled(
  text: string,
  question: BuildPictureGraphQuestion,
  id: string,
  direction: 1 | -1,
): string {
  const counts = splitFilled(text, question);
  const now = counts.get(id);
  if (now === undefined) return text;
  counts.set(id, Math.min(FILL_MOST, Math.max(0, now + direction)));
  return question.rows
    .map((row) => `${row.id}=${String(counts.get(row.id) ?? row.start)}`)
    .join('\n');
}

// What is sent, in one agreed order. Null while a row to fill is empty.
export function fillOrder(
  text: string,
  question: BuildPictureGraphQuestion,
): string[] | null {
  const counts = splitFilled(text, question);
  if (question.rows.some((row) => (counts.get(row.id) ?? 0) === 0)) return null;
  return question.rows
    .map((row) => rowEntry(row.id, counts.get(row.id) ?? 0))
    .sort();
}

// The graph with the child's pictures in it.
export function filledGraph(
  text: string,
  question: BuildPictureGraphQuestion,
): PictureGraph {
  const counts = splitFilled(text, question);
  return {
    ...question.graph,
    rows: question.graph.rows.map((row, index) => ({
      ...row,
      count: counts.get(String(index)) ?? row.count,
    })),
  };
}

// An answer key from the question bank, for whatever loads the questions.
// `answer` gives each row to fill by its label and the number of pictures it
// ends with ({ "no": 7 }). `picture` is the graph as the bank writes it.
// Returns the list for the question's `fill` and its key, or null when the
// answer does not fit the picture: a row that is not there, a count the
// child could not reach, or every row already showing its answer.
export function buildPictureGraphKey(
  answer: unknown,
  picture: unknown,
): { fill: string[]; key: string[] } | null {
  const graph = readPictureGraph(picture);
  if (!graph || typeof answer !== 'object' || answer === null) return null;
  if (Array.isArray(answer)) return null;
  const fill = Object.keys(answer);
  const rows = rowsOf(graph, fill);
  if (!rows) return null;
  const wanted = answer as Record<string, unknown>;
  const key: string[] = [];
  for (const row of rows) {
    const count = wanted[row.label];
    if (typeof count !== 'number' || !Number.isInteger(count)) return null;
    if (count < 1 || count > FILL_MOST) return null;
    key.push(rowEntry(row.id, count));
  }
  if (rows.every((row) => wanted[row.label] === row.start)) return null;
  return { fill, key: key.sort() };
}
